import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AirplaneTilt } from "@phosphor-icons/react";
import { motion } from "framer-motion";

export default function Login() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    let result;
    if (isLogin) {
      result = await login(email, password);
    } else {
      result = await register(email, password, fullName);
    }
    setLoading(false);
    if (result.success) {
      navigate("/");
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-navy flex">
      {/* Left side - branding */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden items-center justify-center"
        style={{
          backgroundImage: `url(https://images.unsplash.com/photo-1767895193055-7d5635f293cb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHxhaXJwbGFuZSUyMHdpbmclMjBkYXJrJTIwbmlnaHQlMjBza3l8ZW58MHx8fHwxNzc2MDg0MjI5fDA&ixlib=rb-4.1.0&q=85)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-navy/70 backdrop-blur-sm" />
        <div className="relative z-10 text-center px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="w-20 h-20 rounded-2xl bg-ci-orange/20 border border-ci-orange/30 flex items-center justify-center mx-auto mb-8">
              <AirplaneTilt size={48} weight="duotone" className="text-ci-orange" />
            </div>
            <h1 className="font-syne font-bold text-4xl text-white tracking-tight mb-4">
              CI Fare Tracker
            </h1>
            <p className="text-text-secondary text-lg max-w-md mx-auto leading-relaxed">
              Real-time airline fare comparison dashboard for the China Airlines Auckland station team.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Right side - form */}
      <div className="flex-1 lg:max-w-xl flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="w-full max-w-sm"
        >
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-ci-orange/20 flex items-center justify-center">
              <AirplaneTilt size={24} weight="duotone" className="text-ci-orange" />
            </div>
            <h1 className="font-syne font-bold text-xl text-white">CI Fare Tracker</h1>
          </div>

          <h2 className="font-syne font-bold text-2xl text-white mb-2">
            {isLogin ? "Welcome back" : "Create account"}
          </h2>
          <p className="text-text-secondary mb-8">
            {isLogin ? "Sign in to access fare data" : "Register for a new account"}
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <div>
                <Label htmlFor="fullName" className="text-text-secondary text-sm mb-2 block">Full Name</Label>
                <Input
                  id="fullName"
                  data-testid="register-name-input"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Enter your full name"
                  required={!isLogin}
                  className="bg-card-bg border-[rgba(255,255,255,0.08)] text-white placeholder:text-text-secondary/50 h-12"
                />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-text-secondary text-sm mb-2 block">Email</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="bg-card-bg border-[rgba(255,255,255,0.08)] text-white placeholder:text-text-secondary/50 h-12"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-text-secondary text-sm mb-2 block">Password</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required
                className="bg-card-bg border-[rgba(255,255,255,0.08)] text-white placeholder:text-text-secondary/50 h-12"
              />
            </div>

            {error && (
              <div data-testid="auth-error" className="bg-danger/10 border border-danger/30 rounded-xl px-4 py-3 text-danger text-sm">
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full h-12 bg-ci-orange hover:bg-ci-orange/90 text-white font-semibold rounded-xl transition-all duration-200"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : isLogin ? "Sign In" : "Create Account"}
            </Button>
          </form>

          <p className="text-center mt-6 text-text-secondary text-sm">
            {isLogin ? "Don't have an account?" : "Already have an account?"}
            <button
              data-testid="toggle-auth-mode"
              onClick={() => { setIsLogin(!isLogin); setError(""); }}
              className="text-ci-orange hover:text-ci-orange/80 ml-1 font-medium"
            >
              {isLogin ? "Register" : "Sign in"}
            </button>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
